'use client';

import * as React from 'react';
import { cn } from './cn';

export interface ProgressBarProps extends React.HTMLAttributes<HTMLDivElement> {
  /** 0–100. Omit (or pass `null`) for the indeterminate state. */
  value?: number | null;
  label?: React.ReactNode;
  /** Right-hand readout, e.g. "12 / 40 עמודים". */
  hint?: React.ReactNode;
  /** Show the percentage next to the label. */
  showValue?: boolean;
  size?: 'sm' | 'md';
  variant?: 'accent' | 'success' | 'danger';
}

const FILL = {
  accent: 'bg-accent',
  success: 'bg-success',
  danger: 'bg-danger',
} as const;

/**
 * Determinate or indeterminate progress. Grows from the reading start, so an
 * export bar fills right-to-left in Hebrew.
 */
export const ProgressBar = React.forwardRef<HTMLDivElement, ProgressBarProps>(function ProgressBar(
  { value = null, label, hint, showValue, size = 'md', variant = 'accent', className, ...props },
  ref,
) {
  const indeterminate = value === null || value === undefined;
  const pct = indeterminate ? 0 : Math.max(0, Math.min(100, value));

  return (
    <div ref={ref} className={cn('flex w-full flex-col gap-1.5', className)} {...props}>
      {(label || hint || showValue) && (
        <div className="flex items-baseline justify-between gap-3 text-xs">
          {label ? <span className="font-medium text-ink">{label}</span> : <span />}
          <span className="tabular-nums text-ink-2" dir="ltr">
            {hint ?? (showValue && !indeterminate ? `${Math.round(pct)}%` : null)}
          </span>
        </div>
      )}
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={indeterminate ? undefined : Math.round(pct)}
        aria-label={typeof label === 'string' ? label : undefined}
        className={cn(
          'relative w-full overflow-hidden rounded-full bg-paper-3',
          size === 'sm' ? 'h-1.5' : 'h-2',
        )}
      >
        {indeterminate ? (
          <div
            className={cn('absolute inset-y-0 w-2/5 rounded-full', FILL[variant])}
            style={{
              animation: 'k-progress-indeterminate 1.4s var(--ease-in-out-soft) infinite',
            }}
          />
        ) : (
          <div
            className={cn(
              'absolute inset-y-0 start-0 rounded-full transition-[width] duration-(--duration-slow) ease-out-soft',
              FILL[variant],
            )}
            style={{ width: `${pct}%` }}
          />
        )}
      </div>
      <style>{'@keyframes k-progress-indeterminate{0%{inset-inline-start:-40%}100%{inset-inline-start:100%}}'}</style>
    </div>
  );
});
